import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { es, enUS, pt } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Analysis } from '@/types/sections';
import { API_BASE_URL } from '@/config/api';
import axios from 'axios';

interface AnalysisDatesProps {
  analysis: Analysis;
  onAnalysisUpdate?: (updatedAnalysis: Analysis) => void;
  isRecording?: boolean;
  isPaused?: boolean;
}

export const AnalysisDates: React.FC<AnalysisDatesProps> = ({ 
  analysis, 
  onAnalysisUpdate,
  isRecording = false,
  isPaused = false
}) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [isForcing, setIsForcing] = useState(false);
  const [localAnalysis, setLocalAnalysis] = useState(analysis);

  // Update local analysis when prop changes
  useEffect(() => {
    setLocalAnalysis(analysis);
  }, [analysis]);

  // Don't render if analysis is not finished
  if (!localAnalysis.attributes.finished) {
    return null;
  }

  const getLocale = () => {
    switch (i18n.language) {
      case 'es': return es;
      case 'en': return enUS;
      case 'pt': return pt;
      default: return es;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy HH:mm:ss', { locale: getLocale() });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const forceFinishAnalysis = async () => {
    setIsForcing(true);
    try {
      const token = localStorage.getItem('token');
      const endDate = new Date().toISOString();
      
      const response = await axios.put(
        `${API_BASE_URL}/analyses/${analysis.id}`,
        {
          data: {
            finished: true,
            end: endDate
          }
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedAnalysis = {
        ...localAnalysis,
        attributes: {
          ...localAnalysis.attributes,
          finished: true,
          end: endDate
        }
      };

      // Update local state immediately
      setLocalAnalysis(updatedAnalysis);

      if (onAnalysisUpdate) {
        onAnalysisUpdate(updatedAnalysis);
      }

      toast({
        title: t('success'),
        description: t('analysis_forced_closed'),
      });
    } catch (error) {
      console.error('Error forcing analysis closure:', error);
      toast({
        title: t('error'),
        description: t('error_forcing_analysis_closure'),
        variant: 'destructive'
      });
    } finally {
      setIsForcing(false);
    }
  };

  const startDate = localAnalysis.attributes.start || localAnalysis.attributes.createdAt;
  const endDate = localAnalysis.attributes.end;
  const isFinished = localAnalysis.attributes.finished;

  // Check if analysis should be force closed (has no end date but should be finished)
  const shouldForceClose = !endDate && !isFinished && !isPaused;

  return (
    <div className="border-t pt-6 mt-6 space-y-3">
      <h3 className="text-sm font-medium text-gray-900">{t('analysis_dates')}</h3>
      
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex justify-between">
          <span className="font-medium">{t('start_date')}:</span>
          <span>{formatDate(startDate)}</span>
        </div>
        
        {endDate && (
          <div className="flex justify-between">
            <span className="font-medium">{t('end_date')}:</span>
            <span>{formatDate(endDate)}</span>
          </div>
        )}
        
        {!endDate && !isPaused && (
          <div className="flex justify-between items-center">
            <span className="font-medium">{t('end_date')}:</span>
            <button 
              onClick={forceFinishAnalysis}
              disabled={isForcing}
              className="text-orange-600 hover:text-orange-800 underline cursor-pointer disabled:opacity-50"
            >
              {isForcing ? t('closing') : t('not_finished')}
            </button>
          </div>
        )}
      </div>

      {shouldForceClose && !isRecording && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mt-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-yellow-800 font-medium">{t('analysis_not_closed_properly')}</p>
              <p className="text-xs text-yellow-700 mt-1">{t('force_close_description')}</p>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              onClick={forceFinishAnalysis}
              disabled={isForcing}
              className="ml-3"
            >
              {isForcing ? t('closing') : t('force_close')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
